import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import ThemeToggle from '../components/ThemeToggle';
import './About.css';

const PRIVACY_URL = 'https://gobunnyy.com/privacy-policy';
const TERMS_URL = 'https://gobunnyy.com/terms-of-use';
const COMPANY_URL = 'https://gobunnyy.com';

/**
 * Public product page for OAuth brand verification — /about (no login).
 * Must stay crawlable without auth and name the app for OAuth brand verification.
 */
const About = () => {
  useEffect(() => {
    document.title = 'About · GoSuite by GoBunnyy';
  }, []);

  return (
    <div className="about-page">
      <div className="about-atmosphere" aria-hidden="true">
        <span className="about-orb about-orb--a" />
        <span className="about-orb about-orb--b" />
        <span className="about-grid" />
      </div>

      <header className="about-topbar">
        <Link to="/about" className="about-brand-link" aria-label="GoSuite by GoBunnyy">
          <BrandLogo variant="navbar" />
        </Link>
        <div className="about-topbar-actions">
          <ThemeToggle />
          <Link to="/login" className="btn btn-primary about-signin-btn">
            Sign in
          </Link>
        </div>
      </header>

      <main className="about-main">
        <section className="about-hero" aria-labelledby="about-app-name">
          <p className="about-eyebrow">Product overview</p>
          <h1 id="about-app-name">GoSuite by GoBunnyy</h1>
          <p className="about-lead">
            GoSuite is the commercial workspace from GoBunnyy. Teams use it for customers, sales,
            delivery, procurement, and finance—with a login that is independent from GoDash
            recruiting.
          </p>
          <div className="about-hero-actions">
            <Link to="/login" className="btn btn-primary">
              Sign in to GoSuite
            </Link>
            <a
              href={COMPANY_URL}
              className="btn btn-secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              About GoBunnyy
            </a>
          </div>
        </section>

        <section className="about-purpose" aria-labelledby="about-purpose-heading">
          <h2 id="about-purpose-heading">What GoSuite is for</h2>
          <p>
            GoSuite helps GoBunnyy consultants manage commercial delivery: customers and pipeline,
            service packages, projects, buying and vendors, and accounting.
          </p>
          <ul className="about-feature-list">
            <li>
              <strong>Revenue</strong> — customers, sales pipeline, requisitions, client POs, sales
              documents, and services
            </li>
            <li>
              <strong>Delivery</strong> — projects and board
            </li>
            <li>
              <strong>Procurement &amp; finance</strong> — vendors, buying, accounting, and consultant
              payouts
            </li>
            <li>
              <strong>Google Sign-In</strong> — authenticate with your Google account. Calendar
              scheduling for recruiting lives on GoDash.
            </li>
          </ul>
        </section>

        <section className="about-google" aria-labelledby="about-google-heading">
          <h2 id="about-google-heading">How we use Google data</h2>
          <p>
            When you authorize GoSuite, we use your Google account to authenticate you. We use
            profile information such as name, email, and photo to identify your workspace account.
            Use of Google user data is limited to providing these features, as described in our
            privacy policy.
          </p>
        </section>
      </main>

      <footer className="about-footer">
        <nav className="about-legal" aria-label="Legal">
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <span className="about-legal-sep" aria-hidden="true">
            ·
          </span>
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
            Terms of Use
          </a>
          <span className="about-legal-sep" aria-hidden="true">
            ·
          </span>
          <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">
            gobunnyy.com
          </a>
        </nav>
        <p className="about-footer-note">GoSuite by GoBunnyy — ERP &amp; commercial operations</p>
      </footer>
    </div>
  );
};

export default About;
